import { useState, useEffect } from "react";
import { useUser } from "@clerk/react";
import { TrendingUp, Heart, Filter } from "lucide-react";
import { LazyVideo } from "@/components/ui/lazy-video";
import { VideoModal } from "@/components/ui/video-modal";
import { cn } from "@/lib/utils";
import { trackViewContent } from "@/services/analytics";

const S3 = "https://couchstackdata.s3.us-east-1.amazonaws.com/samples";

const CATEGORIES = ["All", "Abstract", "Cyberpunk", "Surreal", "Fantasy", "Anime", "Oil Painting", "Watercolor", "Dark Fantasy"];

const GALLERY = [
  { title: "Fluid Gold", style: "Abstract", views: "21.3K", likes: "3.2K", video: `${S3}/Abstract_1.mp4` },
  { title: "Color Burst", style: "Abstract", views: "7.4K", likes: "1.1K", video: `${S3}/Abstract_2.mp4` },
  { title: "Ink Bloom", style: "Abstract", views: "14.6K", likes: "2.3K", video: `${S3}/Abstract_3.mp4` },
  { title: "Prism Light", style: "Abstract", views: "19.4K", likes: "4.6K", video: `${S3}/Abstract_4.mp4` },
  { title: "Neon Alley", style: "Cyberpunk", views: "8.9K", likes: "1.4K", video: `${S3}/CyberPunk_1.mp4` },
  { title: "Night Circuit", style: "Cyberpunk", views: "14.2K", likes: "2.1K", video: `${S3}/Cyberpunk_2.mp4` },
  { title: "Cyber Dawn", style: "Cyberpunk", views: "11.5K", likes: "1.8K", video: `${S3}/Cyberpunk_3.mp4` },
  { title: "Neon Rain", style: "Cyberpunk", views: "16.7K", likes: "3.0K", video: `${S3}/Cyberpunk_4.mp4` },
  { title: "Melting Time", style: "Surreal", views: "15.1K", likes: "2.8K", video: `${S3}/Surreal_1.mp4` },
  { title: "Dream Shift", style: "Surreal", views: "9.8K", likes: "1.6K", video: `${S3}/Surreal_2.mp4` },
  { title: "Mirror World", style: "Surreal", views: "13.4K", likes: "2.4K", video: `${S3}/Surreal_3.mp4` },
  { title: "Gravity Bend", style: "Surreal", views: "11.2K", likes: "1.9K", video: `${S3}/Surreal_4.mp4` },
  { title: "Forest Spirit", style: "Fantasy", views: "6.7K", likes: "1.1K", video: `${S3}/Fantasy_1.mp4` },
  { title: "Enchanted", style: "Fantasy", views: "22.5K", likes: "4.2K", video: `${S3}/Fantasy_2.mp4` },
  { title: "Lost Kingdom", style: "Fantasy", views: "17.9K", likes: "3.3K", video: `${S3}/Fantasy_3.mp4` },
  { title: "Dragon Peak", style: "Fantasy", views: "28.1K", likes: "5.7K", video: `${S3}/Fantasy_4.mp4` },
  { title: "Cherry Blossoms", style: "Anime", views: "31.8K", likes: "5.1K", video: `${S3}/Anime_1.mp4` },
  { title: "Sakura Wind", style: "Anime", views: "19.7K", likes: "3.5K", video: `${S3}/Anime_2.mp4` },
  { title: "Moonlit Duel", style: "Anime", views: "26.4K", likes: "4.8K", video: `${S3}/Anime_3.mp4` },
  { title: "Sky Temple", style: "Anime", views: "23.6K", likes: "4.1K", video: `${S3}/Anime_4.mp4` },
  { title: "Storm Light", style: "Oil Painting", views: "12.4K", likes: "2.1K", video: `${S3}/OilPainting_1.mp4` },
  { title: "Golden Hour", style: "Oil Painting", views: "16.3K", likes: "2.9K", video: `${S3}/OilPainting_2.mp4` },
  { title: "Still Life", style: "Oil Painting", views: "8.7K", likes: "1.5K", video: `${S3}/OilPainting_3.mp4` },
  { title: "Harvest Moon", style: "Oil Painting", views: "14.1K", likes: "2.6K", video: `${S3}/OilPainting_4.mp4` },
  { title: "Venice Dusk", style: "Watercolor", views: "9.2K", likes: "1.9K", video: `${S3}/Watercolour_1.mp4` },
  { title: "Rain Wash", style: "Watercolor", views: "11.8K", likes: "2.2K", video: `${S3}/Watercolour_2.mp4` },
  { title: "Autumn Mist", style: "Watercolor", views: "10.1K", likes: "1.7K", video: `${S3}/Watercolour_3.mp4` },
  { title: "Morning Bloom", style: "Watercolor", views: "13.5K", likes: "2.5K", video: `${S3}/Watercolour_4.mp4` },
  { title: "Gothic Ruin", style: "Dark Fantasy", views: "18.5K", likes: "3.7K", video: `${S3}/DarkFantasy_1.mp4` },
  { title: "Shadow Keep", style: "Dark Fantasy", views: "24.1K", likes: "4.4K", video: `${S3}/DarkFantasy_2.mp4` },
  { title: "Blood Moon", style: "Dark Fantasy", views: "20.6K", likes: "3.9K", video: `${S3}/DarkFantasy_3.mp4` },
  { title: "Abyss Gate", style: "Dark Fantasy", views: "15.8K", likes: "2.8K", video: `${S3}/DarkFantasy_4.mp4` },
];

export function ExplorePage() {
  const { user } = useUser();
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  useEffect(() => {
    trackViewContent("Explore", user?.id, { funnel_stage: "explore" });
  }, []);

  const filtered = activeCategory === "All"
    ? GALLERY
    : GALLERY.filter((g) => g.style === activeCategory);

  return (
    <div className="p-5 md:p-8 max-w-6xl mx-auto">
      <div className="mb-6 animate-fade-in-up">
        <h1 className="text-2xl font-extrabold text-[#2d3127] tracking-tight mb-1">Explore</h1>
        <p className="text-sm text-[#8a8570]">Discover AI Art and AI Video from the community</p>
      </div>

      <div className="flex items-center gap-2 mb-6 overflow-x-auto no-scrollbar pb-1 animate-fade-in-up delay-100">
        <div className="size-8 rounded-xl bg-white/60 backdrop-blur-sm border border-white/50 flex items-center justify-center text-[#8a8570] shrink-0">
          <Filter className="size-3.5" />
        </div>
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={cn(
              "px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all",
              activeCategory === cat
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200/60"
                : "text-[#8a8570] hover:text-[#5a5545] hover:bg-white/40",
            )}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 animate-fade-in delay-200">
        {filtered.map((item, i) => (
          <div
            key={i}
            onClick={() => setActiveVideo(item.video)}
            className="video-card group"
          >
            <div className="relative w-full aspect-square bg-[#f0ebe0] overflow-hidden">
              <LazyVideo src={item.video} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors" />
              <span className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-black/40 backdrop-blur-sm text-white/80">
                {item.style}
              </span>
            </div>
            <div className="px-2.5 py-2">
              <p className="text-[11px] sm:text-[12px] text-[#5a5545] font-medium mb-1">{item.title}</p>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1 text-[9px] sm:text-[10px] text-[#8a8570]">
                  <TrendingUp className="size-2.5" />
                  {item.views}
                </span>
                <span className="flex items-center gap-1 text-[9px] sm:text-[10px] text-[#8a8570]">
                  <Heart className="size-2.5" />
                  {item.likes}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {activeVideo && <VideoModal src={activeVideo} onClose={() => setActiveVideo(null)} />}
    </div>
  );
}
